/*
 * Single source of truth for the /guide section: routes, topic tabs, and SEO metadata.
 *
 * Adding a guide article means adding one entry here — the route, the topic tab, the
 * sitemap entry and the llms.txt entry are all derived from this file.
 *
 * IMPORTANT: scripts/generate-seo-files.js parses this file textually at build time. It
 * reads only `slug` and `title` (the llms.txt entry's name), so keep those on their own
 * lines as plain quoted string literals: `slug` single-quoted with no apostrophes, and
 * `title` single- or double-quoted, with any matching quote inside it escaped. The build
 * fails on a `title` it cannot read. `navLabel` and `description` are never parsed and
 * are free-form.
 *
 * Deliberately free of Angular imports so it stays trivially parseable and can never
 * form an import cycle with guide.component.ts.
 */

export interface GuideTabBase {
  /** Text shown on the topic tab. */
  navLabel: string;
  /** Whether this entry gets a topic tab. False for sub-articles and the /guide index. */
  showInNav: boolean;
  /** Highlights the tab while GuideComponent.selectedArticle matches. */
  article: string | null;
}

/** A page served under /guide. */
export interface GuidePage extends GuideTabBase {
  kind: 'page';
  /** URL segment. An empty slug is the /guide index itself. */
  slug: string;
  /** Drives GuideComponent.selectedSubArticle. Null for top-level articles. */
  subArticle: string | null;
  /** The complete <title>, used verbatim — the site-wide suffix is not appended. */
  title: string;
  /** <meta name="description"> and og:description. Empty falls back to the site default. */
  description: string;
}

/** A topic tab that navigates out of the /guide section entirely. */
export interface GuideExternalTab extends GuideTabBase {
  kind: 'link';
  /** Absolute app route, e.g. '/enterprise'. */
  routerLink: string;
  article: string | null;
}

export type GuideEntry = GuidePage | GuideExternalTab;

// Array order defines topic tab order. Sub-articles sit directly after their parent.
//
// TODO: `title` and `description` below are placeholders. Each title is distinct and
// short enough to survive search-result truncation, and each empty description falls
// back to the site-wide default, so nothing is worse than before — but every page wants
// real copy written for it.
export const GUIDE_ENTRIES: GuideEntry[] = [
  {
    kind: 'page',
    slug: '',
    navLabel: 'Guide Home',
    showInNav: true,
    article: null,
    subArticle: null,
    title: 'Dash Guides: Meeting Room Display Setup & Support',
    description: 'Step-by-step guides for installing Dash on iPad, Android and smart TVs, connecting Google Workspace or Microsoft 365, and deploying at scale with MDM.'
  },
  {
    kind: 'page',
    slug: 'quickStart',
    navLabel: 'Quick Start Guide',
    showInNav: true,
    article: 'quickStart',
    subArticle: null,
    title: 'Dash Quick Start: Room Display Running in 10 Minutes',
    description: 'Install Dash, connect a calendar, pick your room and mount the device. Free read-only plan, no card required to get a display working.'
  },
  {
    kind: 'page',
    slug: 'officeSetup',
    navLabel: 'Microsoft 365 Setup',
    showInNav: true,
    article: 'officeSetup',
    subArticle: null,
    title: 'Microsoft 365 Setup: Connect Dash to Room Mailboxes',
    description: 'Two ways to connect Dash to Microsoft 365 - an enterprise application for organisation-wide access, or a service account for smaller teams. Compare both.'
  },
  {
    kind: 'page',
    slug: 'o365-powershell',
    navLabel: 'Office 365 Powershell',
    showInNav: false,
    article: 'officeSetup',
    subArticle: 'o365-powershell',
    title: 'Show meeting titles in Microsoft 365 with PowerShell',
    description: 'PowerShell commands for setting your room mailbox defaults to work with Dash.'
  },
  {
    kind: 'page',
    slug: 'o365-enterprise-application',
    navLabel: 'Enterprise Application',
    showInNav: false,
    article: 'officeSetup',
    subArticle: 'o365-enterprise-application',
    title: 'Connect Dash to Microsoft 365 with an Enterprise App',
    description: 'Grant Dash access to every room mailbox in your tenant. Set up once by a global admin, new room and equipment mailboxes are picked up automatically.'
  },
  {
    kind: 'page',
    slug: 'o365-service-account',
    navLabel: 'Service Account',
    showInNav: false,
    article: 'officeSetup',
    subArticle: 'o365-service-account',
    title: 'Connect Dash to Microsoft 365 Using a Service Account',
    description: 'Connect Dash to room mailboxes with a single Microsoft 365 account. Includes Exchange delegate setup and calendar sharing permissions. Best for small teams.'
  },
  {
    kind: 'page',
    slug: 'googleSetup',
    navLabel: 'Google Setup',
    showInNav: true,
    article: 'googleSetup',
    subArticle: null,
    title: 'Google Workspace Setup: Connect Dash to Room Calendars',
    description: 'Two ways to connect Dash to Google Workspace resource calendars - a service account for organisation-wide access, or a single Google account for small teams.  Compare both.'
  },
  {
    kind: 'page',
    slug: 'google-service-account',
    navLabel: 'Google Workspace service account',
    showInNav: false,
    article: 'googleSetup',
    subArticle: 'google-service-account',
    title: 'Connect Dash to Google Workspace with a Service Account',
    description: 'Give Dash access to all your Workspace resource calendars using a service account and domain-wide delegation, configured in the Google Admin console.'
  },
  {
    kind: 'page',
    slug: 'google-personal-account',
    navLabel: 'Google Workspace or personal account',
    showInNav: false,
    article: 'googleSetup',
    subArticle: 'google-personal-account',
    title: 'Connect Dash to a Personal Google Calendar',
    description: 'Share your meeting room calendars with one Google account and connect it to Dash. The right method for small businesses and personal Google accounts.'
  },
  {
    kind: 'page',
    slug: 'exchangeCalendarSetup',
    navLabel: 'Exchange & others setup',
    showInNav: true,
    article: 'exchangeCalendarSetup',
    subArticle: null,
    title: 'Dash for Exchange: meeting room displays with on-prem Exchange or CalDAV',
    description: 'Set up Dash to work with a calendar service not directly supported by our direct integrations (such as an on-prem Exchange Server or CalDAV)'
  },
  {
    kind: 'page',
    slug: 'deviceSetup',
    navLabel: 'Device Setup',
    showInNav: true,
    article: 'deviceSetup',
    subArticle: null,
    title: 'Set Up Dash on iPad or Android device',
    description: 'Get Dash running your device. Step-by-step setup for iPad, Android, and Fire tablets, including kiosk-mode lockdown.'
  },
  {
    kind: 'page',
    slug: 'usingDash',
    navLabel: 'Using Dash',
    showInNav: true,
    article: 'usingDash',
    subArticle: null,
    title: 'Using Dash: Room Display and Booking Controls',
    description: 'Book a room from the display, extend or end a meeting early, release no-show bookings automatically, and read room status at a glance.'
  },
  {
    kind: 'page',
    slug: 'practices',
    navLabel: 'Practices',
    showInNav: true,
    article: 'practices',
    subArticle: null,
    title: 'Meeting Room Display Best Practices',
    description: 'Practical advice for getting more usable hours out of your meeting rooms.'
  },
  {
    kind: 'link',
    routerLink: '/enterprise',
    navLabel: 'Enterprise',
    showInNav: true,
    article: 'enterprise'
  }
];

/** Every page served under /guide, in route-declaration order. */
export const GUIDE_PAGES: GuidePage[] =
  GUIDE_ENTRIES.filter((entry): entry is GuidePage => entry.kind === 'page');

/** The topic tabs shown in the guide header, in display order. */
export const GUIDE_TABS: GuideEntry[] =
  GUIDE_ENTRIES.filter((entry) => entry.showInNav);
